import { useContext } from "react";
import styled from "styled-components";
import { formatNumber } from "utils/NumberUtils";
import { NotosansjpMediumCloudBurst12px } from "../../styledMixins";

import { PageEditorContext } from "components/PageEditor";
import { PortfolioContext } from "components/Portfolio";

const assetTypeOrder = [
  "国内債券",
  "国内株式",
  "外国債券",
  "外国株式",
  "オルタナティブ",
  "マルチアセット",
  "一般勘定",
  "短期資産",
  "ヘッジ外国債券",
  "債券",
  "グローバル債券",
  "ヘッジ外国株式",
  "株式",
  "グローバル株式",
  "ヘッジファンド",
  "プライベートアセット",
  "不動産",
  "インフラ",
  "プライベートエクイティ",
  "その他1",
  "その他2",
  "その他3",
  "その他4",
  "その他5",
  "その他6",
  "その他7",
  "その他8",
];

const convert = (productFactorExposure, assetNames) => {
  const result = { ...productFactorExposure };
  result.assetTypeName =
    assetNames[productFactorExposure.asset_type] ||
    productFactorExposure.asset_type;
  const factorExposuresByMainCategory =
    productFactorExposure.factor_exposures_by_main_category;
  for (let factorExposure of factorExposuresByMainCategory) {
    result[factorExposure.main_category] = factorExposure.exposure_value;
  }
  return result;
};

const extractProductExposure = (analysisResult, assetNames) => {
  const productFactorExposureResult =
    analysisResult?.product_factor_exposure_result;
  const productFactorExposures =
    productFactorExposureResult.product_factor_exposures;
  const convertedProductFactorExposures = productFactorExposures.map(
    (productFactorExposure) => convert(productFactorExposure, assetNames)
  );
  return convertedProductFactorExposures.sort((a, b) =>
    a.asset_type === b.asset_type
      ? a.index - b.index
      : assetTypeOrder.indexOf(a.asset_type) -
        assetTypeOrder.indexOf(b.asset_type)
  );
};

const maxProductsForProductFactorExposures = 20;

const ProductFactorExposures = (props) => {
  const setRendered = props.setRendered;
  const productIndex = props.productIndex || 0;
  const { assetNames: portAssetNames, analysisResult: portAnalysisResult } =
    useContext(PortfolioContext);
  const { assetNames: pageAssetNames, analysisResult: pageAnalysisResult } =
    useContext(PageEditorContext);
  const assetNames = portAssetNames || pageAssetNames;

  const analysisResult = portAnalysisResult || pageAnalysisResult;
  if (!analysisResult) return <></>;

  const data = extractProductExposure(analysisResult, assetNames);

  if (!data) return <></>;

  // targetDataに、dataのproductIndex番目らmaxProductsForProductFactorExposures個分のデータを格納
  const targetData = data.slice(
    productIndex,
    productIndex + maxProductsForProductFactorExposures
  );

  const asIsColumns = [
    "国内株式",
    "海外株式",
    "国内金利",
    "海外金利",
    "為替",
    "クレジット",
    "実物他",
    "上場REIT",
    "プライベートアセット",
    "戦略α",
  ];
  const percentColumns = ["manager_alpha_return", "manager_alpha_risk"];

  const makeTable = (data) => {
    if (data.length === 0)
      return (
        <div>WS内のプロダクト数が少なく、表示対象プロダクトがありません</div>
      );
    setRendered(true);
    return (
      <div
        style={{
          flexGrow: 1,
          // width: "100%",
          // flexShrink: 1,
        }}
      >
        <TableTag>
          <thead>
            <tr>
              <th rowSpan={2}>No.</th>
              <th rowSpan={2}>資産</th>
              <th rowSpan={2}>プロダクト名</th>
              <th rowSpan={2}>ベンチマーク</th>
              <th colSpan={10}>収益要素の保有割合</th>
              <th colSpan={2} style={{ fontSize: 8 }}>
                αの期待リターンとリスク
              </th>
            </tr>
            <tr className="second-header">
              <th>
                国内
                <br />
                株式
              </th>
              <th>
                海外
                <br />
                株式
              </th>
              <th>
                国内
                <br />
                金利
              </th>
              <th>
                海外
                <br />
                金利
              </th>
              <th>為替</th>
              <th style={{ fontSize: 10 }}>ｸﾚｼﾞｯﾄ</th>
              <th>
                実物
                <br />他
              </th>
              <th>
                上場
                <br />
                REIT
              </th>
              <th>PA</th>
              <th>戦略α</th>
              <th>
                α期待
                <br />
                収益率
              </th>
              <th>αリスク</th>
            </tr>
          </thead>
          <tbody>
            {data.map((record, i) => (
              <tr key={`${i}`}>
                {" "}
                {/* No. */}
                <td style={{ textAlign: "center" }}>{i + 1 + productIndex}</td>
                <td style={{ textAlign: "center" }}>
                  {/* 資産 */}
                  {record.assetTypeName}
                </td>
                <td
                  style={{
                    textAlign: "left",
                    minWidth: 200,
                    maxWidth: 200,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    fontSize: 10,
                  }}
                >
                  {/* プロダクト名 */}
                  {/* プロダクト名が25文字を超える場合は25文字+ ... と表示 */}
                  {record.product_name.length > 25
                    ? record.product_name.slice(0, 25) + "..."
                    : record.product_name}
                </td>
                <td
                  style={{
                    textAlign: "left",
                    minWidth: 180,
                    maxWidth: 180,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    fontSize: 10,
                  }}
                >
                  {/* ベンチマーク */}
                  {/* ベンチマーク名が20文字を超える場合は20文字+ ... と表示 */}
                  {record.benchmark_name.length > 20
                    ? record.benchmark_name.slice(0, 20) + "..."
                    : record.benchmark_name}
                </td>
                {asIsColumns.map((column) => (
                  <td
                    key={`${i}-${column}`}
                    style={{ color: record[column] < 0 ? "red" : "black" }}
                  >
                    {Math.abs(record[column]) < 0.0001
                      ? ""
                      : formatNumber({
                          v: record[column],
                          numDigits: 2,
                          stringForNonNumber: "",
                          // negativeParen: true,
                        })}
                  </td>
                ))}
                {percentColumns.map((column) => (
                  <td key={`${i}-${column}`}>
                    {formatNumber({
                      v: record[column],
                      n: 100,
                      numDigits: 2,
                      postfix: "%",
                      stringForNonNumber: "",
                    })}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </TableTag>
      </div>
    );
  };

  return (
    <div
      style={{ display: "flex", width: 1800, maxWidth: 1800, minHeight: 112 }}
    >
      {makeTable(targetData)}
    </div>
  );
};

const TableTag = styled.table`
  ${NotosansjpMediumCloudBurst12px}
  font-weight: 400;
  color: #192e55;
  border-collapse: collapse;
  margin: 10px 0 40px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  & tr {
    border-bottom: 1px solid #dddddd;
  }
  & td,
  & th {
    padding: 2px 4px;
    border: 1px solid black;
  }
  & td {
    text-align: right;
  }
  & td.negative {
    color: red;
  }
  & tr.spacer td,
  & tr.spacer th {
    border: 1px solid black;
    border-left: none;
    border-right: none;
  }
  & th.no-border,
  & td.no-border {
    border: none;
  }
  & thead th{
    text-align: center;
  }
  & thead .second-header th{
    width: 50px;
  }
`;

export { maxProductsForProductFactorExposures, ProductFactorExposures };
